import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useVideoService from '../../app/services/hooks/useVideoService';
import useHandleErrorFields from '../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import ButtonSaveForm from '../../components/buttons/ButtonSaveForm';
import ButtonTableAction from '../../components/buttons/ButtonTableAction';
import InputField from '../../components/InputField';
import TextAreaField from '../../components/TextAreaField';
import FileUpload from '../../components/FileUpload';
import SquareCheckbox from '../../components/SquareCheckbox/SquareCheckbox';
import { formatCLP, formatDate } from '../Organization/types';
import { Video } from './types';

const EMPTY = { title: '', description: '', is_free: true, price: '', duration_seconds: '', published: false };

/** Gestión de videos: Monere ve todos; un estudio, los suyos (si su plan lo incluye). */
const VideoManage: React.FC = () => {
    const history = useHistory();
    const { getManageVideos, storeVideo, updateVideo, deleteVideo, fetchingManage, fetchingSaveVideo } = useVideoService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { requestConfirmation } = useReactConfirmAlert();

    const [videos, setVideos] = useState<Video[]>([]);
    const [canPublish, setCanPublish] = useState(false);
    const [maxMb, setMaxMb] = useState(100);
    const [editing, setEditing] = useState<Video | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [thumbnail, setThumbnail] = useState<File | null>(null);

    const load = () => {
        getManageVideos({
            onSuccess: (response) => {
                setVideos(response.data.videos);
                setCanPublish(response.data.can_publish);
                setMaxMb(response.data.max_upload_mb);
            },
            onError: (response) => toast.error(response.message)
        });
    };

    useEffect(() => {
        load();
    }, []);

    const openNew = () => {
        setEditing(null);
        setForm(EMPTY);
        setVideoFile(null);
        setThumbnail(null);
        setErrors({});
        setShowForm(true);
    };

    const openEdit = (video: Video) => {
        setEditing(video);
        setForm({
            title: video.title,
            description: video.description,
            is_free: video.is_free,
            price: video.is_free ? '' : String(video.price),
            duration_seconds: video.duration_seconds ? String(video.duration_seconds) : '',
            published: video.status === 'published'
        });
        setVideoFile(null);
        setThumbnail(null);
        setErrors({});
        setShowForm(true);
    };

    const handleSave = () => {
        const data = new FormData();
        data.append('title', form.title);
        data.append('description', form.description);
        data.append('is_free', form.is_free ? 'true' : 'false');
        data.append('price', form.is_free ? '0' : form.price || '0');
        if (form.duration_seconds) data.append('duration_seconds', form.duration_seconds);
        data.append('status', form.published ? 'published' : 'draft');
        if (thumbnail) data.append('thumbnail', thumbnail);
        if (!editing && videoFile) data.append('video_file', videoFile);

        const events = {
            onSuccess: (response: any) => {
                toast.success(response.message);
                setShowForm(false);
                load();
            },
            onError: (response: any) => toast.error(response.message),
            onFieldError: (response: any) => {
                setErrors(response.data);
                toast.error('Revisa los campos marcados');
            }
        };
        if (editing) updateVideo(editing.id, data, events);
        else storeVideo(data, events);
    };

    const handleDelete = (video: Video) => {
        requestConfirmation({
            title: '¿Eliminar el video?',
            message: 'Si alguien lo compró, se despublica en vez de eliminarse para no quitarle el acceso.',
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => deleteVideo(video.id, {
                        onSuccess: (response) => {
                            toast.success(response.message);
                            load();
                        },
                        onError: (response) => toast.error(response.message)
                    })
                },
                cancelButton: { label: 'Cancelar' }
            }
        });
    };

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Videos', path: '/intranet/videos' },
                    { label: 'Gestionar', isCurrent: true }
                ]}
                title="Gestionar videos"
                description="Sube videos gratuitos o de pago único. El archivo queda privado y solo se reproduce con un enlace temporal."
            />
            <DefaultCard>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted small">{videos.length} video(s)</span>
                    {canPublish ? (
                        <ButtonGeneral onClick={openNew} title="Subir video" icon="fa-upload" size="small" />
                    ) : (
                        <span className="small text-muted">Tu plan no incluye publicar videos.</span>
                    )}
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle" data-testid="videos-table">
                        <thead><tr><th>Video</th><th>Precio</th><th>Estado</th><th className="text-center">Vistas</th><th>Subido</th><th className="text-center">Acciones</th></tr></thead>
                        <tbody>
                            {fetchingManage && videos.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-4"><div className="spinner-border text-primary" /></td></tr>
                            ) : videos.length === 0 ? (
                                <tr><td colSpan={6} className="text-center text-muted py-4">Aún no hay videos.</td></tr>
                            ) : videos.map((video) => (
                                <tr key={video.id}>
                                    <td><span className="fw-bold d-block">{video.title}</span><small className="text-muted">{video.publisher}</small></td>
                                    <td>{video.is_free ? <span className="badge bg-success">Gratis</span> : formatCLP(video.price_with_tax)}</td>
                                    <td><span className={`badge ${video.status === 'published' ? 'bg-primary' : 'bg-secondary'}`}>{video.status_display}</span></td>
                                    <td className="text-center">{video.views_count}</td>
                                    <td><small>{formatDate(video.created_at)}</small></td>
                                    <td className="text-center">
                                        <div className="d-flex justify-content-center gap-1">
                                            <ButtonTableAction callbackFunction={() => history.push(`/intranet/videos/${video.id}`)} title="Ver"
                                                               classIcon="fa-eye" colorIcon="text-info" sizeIcon="16" />
                                            {video.can_manage && (
                                                <>
                                                    <ButtonTableAction callbackFunction={() => openEdit(video)} title="Editar"
                                                                       classIcon="fa-edit" colorIcon="text-warning" sizeIcon="16" />
                                                    <ButtonTableAction callbackFunction={() => handleDelete(video)} title="Eliminar"
                                                                       classIcon="fa-trash" colorIcon="text-danger" sizeIcon="16" />
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </DefaultCard>

            <DefaultModal title={editing ? 'Editar video' : 'Subir video'} show={showForm} size="lg" handleClose={() => setShowForm(false)}
                          actionButton={<ButtonSaveForm callbackSave={handleSave} loading={fetchingSaveVideo} title={editing ? 'Guardar' : 'Subir'} />}>
                <div className="row">
                    <div className="col-12 mb-3">
                        <InputField label="Título" value={form.title} required onChange={(v) => setForm({ ...form, title: v })}
                                    onFocus={() => onFocusRemove('title')} error={fieldErrorMessage('title')} />
                    </div>
                    <div className="col-12 mb-3">
                        <TextAreaField label="Descripción" value={form.description} rows={3} maxLength={5000}
                                       onChange={(v) => setForm({ ...form, description: v })}
                                       onFocus={() => onFocusRemove('description')} error={fieldErrorMessage('description')} />
                    </div>
                    {!editing && (
                        <div className="col-12 mb-3">
                            <label className="form-label fw-bold small">Archivo (MP4, WEBM o MOV, máx. {maxMb} MB)</label>
                            <FileUpload onFileSelect={setVideoFile} currentFile={videoFile} acceptedTypes=".mp4,.webm,.mov" maxSize={maxMb} />
                            {fieldErrorMessage('video_file') && <small className="text-danger">{fieldErrorMessage('video_file')}</small>}
                        </div>
                    )}
                    <div className="col-12 mb-3">
                        <label className="form-label fw-bold small">Miniatura (opcional)</label>
                        <FileUpload onFileSelect={setThumbnail} currentFile={thumbnail} existingFileUrl={editing?.thumbnail_url}
                                    acceptedTypes=".png,.jpg,.jpeg,.webp" maxSize={3} showPreview />
                        {fieldErrorMessage('thumbnail') && <small className="text-danger">{fieldErrorMessage('thumbnail')}</small>}
                    </div>
                    <div className="col-md-6 mb-3">
                        <SquareCheckbox id="is_free" name="is_free" value="is_free" checked={form.is_free}
                                        onChange={(_v: string, checked: boolean) => setForm({ ...form, is_free: checked })}
                                        label="Gratuito" />
                    </div>
                    <div className="col-md-6 mb-3">
                        <SquareCheckbox id="published" name="published" value="published" checked={form.published}
                                        onChange={(_v: string, checked: boolean) => setForm({ ...form, published: checked })}
                                        label="Publicado en el catálogo" />
                    </div>
                    {!form.is_free && (
                        <div className="col-md-6 mb-3">
                            <InputField label="Precio neto (CLP, se suma IVA)" type="number" min="1000" value={form.price}
                                        onChange={(v) => setForm({ ...form, price: v })}
                                        onFocus={() => onFocusRemove('price')} error={fieldErrorMessage('price')} />
                        </div>
                    )}
                    <div className="col-md-6 mb-3">
                        <InputField label="Duración (segundos, opcional)" type="number" min="0" value={form.duration_seconds}
                                    onChange={(v) => setForm({ ...form, duration_seconds: v })}
                                    onFocus={() => onFocusRemove('duration_seconds')} error={fieldErrorMessage('duration_seconds')} />
                    </div>
                </div>
            </DefaultModal>
        </div>
    );
};

export default VideoManage;
